<script>
  import { _ } from "svelte-i18n";
  import { createEventDispatcher, onMount, onDestroy } from "svelte";
  import { writable } from "svelte/store";
  import {
    lastRecording,
    sharedTimer,
    sharedTimerMode,
  } from "../../utilities/DataStore";
  import { getAudioContext, getRecorder } from "../../utilities/AudioSingleton.js";


  let recordings = [];
  let recordAudio = [];
  let recordCount = 0;

  export let audioControlDuration = writable([]);

  // While true the preparation countdown stands still. A READ or QUESTION
  // exercise with a recording of its prompt holds it until the candidate has
  // heard the recording.
  export let holdPrepare = false;

  const workerOptions = {
    OggOpusEncoderWasmPath:
      "https://cdn.jsdelivr.net/npm/opus-media-recorder@latest/OggOpusEncoder.wasm",
    WebMOpusEncoderWasmPath:
      "https://cdn.jsdelivr.net/npm/opus-media-recorder@latest/WebMOpusEncoder.wasm",
  };

  const options = { mimeType: "audio/ogg;codecs=opus" };

  window.MediaRecorder = OpusMediaRecorder;
  let recorder

  // shim for AudioContext when it's not avb.
  let AudioContext = window.AudioContext || window.webkitAudioContext;
  // let audioContext; //audio context to help us record

  // let audioContext = getAudioContext();

  let lastFilename;
  let lastBlob;
  let lastAudio;

  // export let time = 90;
  // let mode = "PREPARE" // PREPARE, RECORDING
  // let remainingTime = time;

  let prepareCountDownTimerObject;
  let countDownTimerObject;
  let countDownTimerText = formatTime($sharedTimer);

  let recordingButtonState = "starting";

  const dispatch = createEventDispatcher();

  function formatTime(time) {
    if (time <= 0) {  // Fixed: Handle zero and negative values
      time = 0;
    }
    return Math.floor(time / 60) + ":" + ((time % 60) + "").padStart(2, "0");
  }

  onMount(async () => {
    countDownTimerText = formatTime($sharedTimer);
    audioControlDuration.subscribe((value) => {
      console.log("audioControlDuration", $audioControlDuration, value);

      dispatch("audioControlDuration", { audioControlDuration: value });
    });
  });

  onDestroy(() => {
    console.log("the component is being destroyed");

    if (prepareCountDownTimerObject){
      clearInterval(prepareCountDownTimerObject);
      prepareCountDownTimerObject = null;
    }

    if (countDownTimerObject) {
      clearInterval(countDownTimerObject);
      countDownTimerObject = null;
    }
  });

  // Restart preparation countdown whenever the app switches back to PREPARE mode.
  $: if ($sharedTimerMode === "PREPARE") {
    prepare();
  }

  function prepare() {
    if (prepareCountDownTimerObject) {
      return;
    }

    prepareCountDownTimerObject = setInterval(function () {
      if (holdPrepare) {
        return;
      }
      let c = $sharedTimer-1;
      sharedTimer.set(c);

      if ($sharedTimer <= 0) {  // Fixed: Changed from < 0 to <= 0
        // remainingTime = time;
        // $sharedTimer = -1
        sharedTimerMode.set("STOP");
        clearInterval(prepareCountDownTimerObject);
        prepareCountDownTimerObject = null;
        dispatch("prepareTimerExpired");
      } else {
        countDownTimerText = formatTime(c);
      }
    }, 1000);
  }

  // Bumped by cancelRecording() so a getUserMedia() still pending from a
  // cancelled recording does not start the recorder afterwards.
  let recordingGeneration = 0;

  export function startRecording() {
    const generation = ++recordingGeneration;
    // remainingTime = time;
    // mode = "RECORDING";
    if (prepareCountDownTimerObject) {
      clearInterval(prepareCountDownTimerObject);
      prepareCountDownTimerObject = null;
    }
    sharedTimerMode.set("RECORDING");

    navigator.mediaDevices
      .getUserMedia({ audio: { channelCount: 1 }, video: false })
      .then((stream) => {
        if (generation !== recordingGeneration) {
          stream.getTracks().forEach((i) => i.stop());
          return;
        }

        // Start recording
        // recorder = getRecorder(stream);
        recorder = new MediaRecorder(stream, options, workerOptions);

        recorder.start();
        // Set record to <audio> when recording will be finished
        recorder.addEventListener("dataavailable", (e) => {
          //   console.log("dataavailable", e);

          createDownloadLink(e.data);
        });
        console.log("Recording started");
        recordingButtonState = "recording";
      })
      .catch(function (err) {
        if (generation !== recordingGeneration) return;
        //enable the record button if getUserMedia() fails
        recordingButtonState = "error";
        console.error("[STT-DIAG] getUserMedia failed", {
          name: err?.name,
          message: err?.message || String(err),
          timestamp: new Date().toISOString(),
        });
        // Surface the capture failure to the parent so it can record an explicit
        // NO_AUDIO result instead of leaving the exercise with no result row.
        dispatch("recordingError", {
          reason: "MIC_DENIED",
          error: err?.name || err?.message || String(err),
        });
      });

    if(countDownTimerObject){
      clearInterval(countDownTimerObject)
    }  

    countDownTimerObject = setInterval(function () {
      let c = $sharedTimer-1;
      sharedTimer.set(c);

      if ($sharedTimer <= 0) {  // Fixed: Changed from < 0 to <= 0 to stop exactly at 0
        stopRecording();
        countDownTimerText = "Finished";
      } else {
        countDownTimerText = formatTime(c);
      }
    }, 1000);
  }

  export function stopRecording() {
    sharedTimerMode.set("STOP");
    clearInterval(countDownTimerObject);
    console.log("stopButton clicked");

    //disable the stop button, enable the record too allow for new recordings
    recordingButtonState = "stop";

    if (recorder) {
      recorder?.stop();
      recorder?.stream.getTracks().forEach((i) => i.stop());
    }
   
    dispatch("recordingStop");
  }

  // Stops capture and the recording countdown without dispatching
  // recordingStop, so nothing is submitted. Safe to call when no recording is
  // running or one was never started.
  export function cancelRecording() {
    recordingGeneration++;
    if (countDownTimerObject) {
      clearInterval(countDownTimerObject);
      countDownTimerObject = null;
    }
    try {
      if (recorder && recorder.state !== "inactive") {
        recorder.stop();
      }
      recorder?.stream?.getTracks().forEach((i) => i.stop());
    } catch (err) {
      console.warn("cancelRecording: recorder stop failed", err);
    }
    recordingButtonState = "stop";
  }

  function createDownloadLink(blob) {
    let url = URL.createObjectURL(blob);
    lastBlob = blob;
    //var au = document.createElement('audio');
    //var li = document.createElement('div');

    //name of .wav file to use during upload and download (without extendion)
    let filename = new Date().toISOString();
    lastFilename = filename + ".ogg";

    const newReocrding = { blob: blob, filename: filename, url: url };
    $lastRecording = newReocrding;

    console.log("newReocrding", newReocrding);

    dispatch("recordingAvailable", newReocrding);
  }

  function clickSubmitCalculate() {
    console.log("clickSubmitCalculate");
  }
</script>

<div class="row">
  <!--   
  <div class="col">
    {#if mode === "PREPARE"}{$_("GET_READY_TIME")}{:else}{$_("REMAINING_TIME")}{/if}
    : <span class="ml-1" style="font-size: xx-large;"
      >{countDownTimerText}</span
    >
  </div> -->

  {#if $lastRecording}
    <div class="col" style="display:none;">
      <audio controls="controls" bind:duration={$audioControlDuration}>
        <track kind="captions" />
        <source src={$lastRecording.url} type="audio/wav" />
      </audio>
    </div>
  {/if}
</div>
